import { useEffect, useState } from 'react';
import { Loader2, MapPin, Plus, Star, Link2, Unlink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useProfileLocation } from '@/contexts/ProfileLocationContext';
import * as bookingApi from '@/services/bookingApi';
import type { LocationProfile } from '@/types';
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded';

export default function LocationsPage() {
  const embedded = useDashboardEmbedded();
  const {
    locations,
    currentProfileId,
    setCurrentProfileId,
    createLocation,
    refresh,
    linkProfile,
    unlinkProfile,
    makePrimary,
    isLoading,
    error,
  } = useProfileLocation();

  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(currentProfileId);
  const [linkedProfiles, setLinkedProfiles] = useState<LocationProfile[]>([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedId(currentProfileId);
  }, [currentProfileId]);

  useEffect(() => {
    if (!selectedId) {
      setLinkedProfiles([]);
      return;
    }

    setLoadingLinked(true);
    bookingApi
      .getLinkedProfiles(selectedId)
      .then(setLinkedProfiles)
      .catch(() => setLinkedProfiles([]))
      .finally(() => setLoadingLinked(false));
  }, [selectedId, locations]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setActionError(null);
    try {
      await createLocation(newName.trim());
      setNewName('');
    } catch (err: unknown) {
      setActionError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to create location'
      );
    } finally {
      setCreating(false);
    }
  };

  const handleMakePrimary = async (id: string) => {
    setActionError(null);
    try {
      await makePrimary(id);
    } catch (err: unknown) {
      setActionError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to set primary location'
      );
    }
  };

  const handleLink = async (targetId: string) => {
    if (!selectedId) return;
    setActionError(null);
    try {
      await linkProfile(selectedId, targetId);
      const linked = await bookingApi.getLinkedProfiles(selectedId);
      setLinkedProfiles(linked);
    } catch (err: unknown) {
      setActionError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to link locations'
      );
    }
  };

  const handleUnlink = async (linkedId: string) => {
    if (!selectedId) return;
    setActionError(null);
    try {
      await unlinkProfile(selectedId, linkedId);
      const linked = await bookingApi.getLinkedProfiles(selectedId);
      setLinkedProfiles(linked);
    } catch (err: unknown) {
      setActionError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to unlink locations'
      );
    }
  };

  const linkableLocations = locations.filter(
    (loc) =>
      loc.id !== selectedId &&
      !linkedProfiles.some((linked) => linked.id === loc.id)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-3xl">
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Locations</h1>
          <p className="text-muted-foreground">
            Manage your business locations and link related profiles for bundled booking.
          </p>
        </div>
      )}

      {(error || actionError) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {actionError || error}
        </div>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Add location
          </CardTitle>
        </CardHeader>
        <CardContent className="flex gap-3">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Business name"
            className="flex-1"
          />
          <Button onClick={handleCreate} disabled={creating || !newName.trim()}>
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
          </Button>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {locations.map((location) => (
          <Card
            key={location.id}
            className={selectedId === location.id ? 'ring-2 ring-primary' : ''}
          >
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-semibold text-foreground">{location.business_name}</h3>
                    {location.is_primary && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full">
                        <Star className="w-3 h-3" />
                        Primary
                      </span>
                    )}
                    {currentProfileId === location.id && (
                      <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                        Active
                      </span>
                    )}
                  </div>
                  {location.location && (
                    <p className="text-sm text-muted-foreground mt-1">{location.location}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Currency: {location.currency || 'EUR'}
                    {location.supported_currencies?.length
                      ? ` · Supported: ${location.supported_currencies.join(', ')}`
                      : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedId(location.id);
                      setCurrentProfileId(location.id);
                    }}
                  >
                    Switch to
                  </Button>
                  {!location.is_primary && (
                    <Button variant="outline" size="sm" onClick={() => handleMakePrimary(location.id)}>
                      Make primary
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedId(location.id)}
                  >
                    Manage links
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedId && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Linked locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingLinked ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {linkedProfiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground mb-4">
                    No linked locations yet. Link another owned location to show bundled services.
                  </p>
                ) : (
                  <ul className="space-y-2 mb-4">
                    {linkedProfiles.map((linked) => (
                      <li
                        key={linked.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <span className="font-medium">{linked.business_name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnlink(linked.id)}
                        >
                          <Unlink className="w-4 h-4 mr-1" />
                          Unlink
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}

                {linkableLocations.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-foreground mb-2">Link another location</p>
                    <div className="flex flex-wrap gap-2">
                      {linkableLocations.map((loc) => (
                        <Button
                          key={loc.id}
                          variant="outline"
                          size="sm"
                          onClick={() => handleLink(loc.id)}
                        >
                          <Link2 className="w-4 h-4 mr-1" />
                          {loc.business_name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <div className="mt-6">
        <Button variant="ghost" onClick={() => refresh()}>
          Refresh
        </Button>
      </div>
    </div>
  );
}
